import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";

import { AgGridReact } from "ag-grid-react";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Tab, Box, Typography, Tooltip, IconButton } from "@mui/material"

import { useDispatch } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import reduxUtil from "@plannel/utils/redux-util";
import StackBarChartCellRenderer from "@plannel/components/rechart/StackBarChartCellRenderer"
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import ChartComposed from "@plannel/components/rechart/ChartComposed";

import {isEqual, isNull, isUndefined, keys, isEmpty, includes, sortBy, indexOf, cloneDeep} from "lodash";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import CodeUtil from "../../pages/utils/code-util";
import GridUtils from "../aggrid/GridUtils";
import numberUtil from "@plannel/utils/number-util";
import dateUtils from "@plannel/utils/date-util";
import { useTheme, alpha } from "@mui/material/styles";
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { FS } from "@plannel/utils/font-util";

// 대시보드 공용 카드 표면(새 DW 룩) — 구조는 그대로 두고 배경/보더/라운드만 입힌다.
const cardSurfaceSx = {
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  borderRadius: 1.5,
  boxSizing: "border-box",
  boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
};
// 카드 제목 — 스킬 폰트 스케일(13/600).
const cardTitleSx = { fontSize: FS.TITLE, fontWeight: 600, color: "text.primary" };
// 컴팩트 탭 — 높이/폰트 축소, 대문자 해제.
const compactTabsSx = {
  minHeight: 32,
  "& .MuiTab-root": { minHeight: 32, py: 0.5, fontSize: FS.SUB, textTransform: "none", color: "text.secondary" },
  "& .Mui-selected": { color: "primary.main" },
};

const INV_TURNOVER = "invTurnover";
const INV_TURNOVER_COGS = "invTurnoverCogs";
const LAST_YEAR_INV_TURNOVER = "lastYearInvTurnover";
const LAST_YEAR_INV_TURNOVER_COGS = "lastYearInvTurnoverCogs";
const RP_INVENTORY = "inventory";
const TOP = "TOP";
const BOTTOM = "BOTTOM";
const YOY = "yoy";
const YOY_CUM = "yoyCum";
const EXCEL_EXPORT = "excelExport";
const RP_DASHBOARD = "rpDashboard";

const actualValMeasures = [
  "actualSales", "salesActual", "salesActualProfit", "actualInv", "cogs", "momPer", "momGPPer", "yoyPer", "yoyGPPer",
  "salesVsPlanPer", "salesVsPlanGPPer", "salesVsBudgetGPPer", "finAchievePerQty", "targetVsInvPer"
];

/** DashboardComponent
 * 1. 상단: TabBox (Stack Bar Chart)
 * 2. 하단: TabChartGridBox (Trend Chart & Grid)
 *
 * @param {String} t i18n translation function
 * @param {String} viewName redux view name
 * @param {String} title component title
 * @param {{[stackTitle]:{[tabTitle]:[{type, hrchyNm, stackBarChartData}]}}} stackBarData stacked bar chart data
 * @param {import("@ag-grid-community/core").ColDef[]} stackBarColumDefs stacked bar chart column definitions
 * @param {{[measureType]:String[]}} allMeasures all available measures by measure type
 * @param {String} measureType current measure type [AMT, QTY, etc..] in CodeUtil.toggleType
 * @param {{[measureType]:[{yyyymmdd, measure, location?, custHrchy?, itemHrchy?, [baseDate]}]}} chartData trend chart data
 * @param {{[measureType]:[{measure, location?, custHrchy?, itemHrchy?, totalFields?, totalCustomValue?, [baseDate]}]}} gridData trend grid data
 * @param {String} currencyCd currency code (e.g., "USD", "EUR") for formatting amounts
 * @param {String} moduleCd [IP, RP, DP] in CodeUtil.module
 */
/** @param {{moduleCd:String}} moduleCd [IP, RP, DP] in CodeUtil.module */
const DashboardComponent = ({ t, viewName, title, stackBarData, stackBarColumDefs, allMeasures = [], measureType, chartData, gridData, moduleCd, currencyCd }) => {

  const HRCHY_GRID = "hrchy_grid";
  const [fullScreenInfo, setFullScreenInfo] = useState({ targetPosition: "", targetTitle: "", fullScreen: false});
  const hrchyGridRef = useRef();

  const defaulHrchysGridMemo = useMemo(() => {
    return DefaultGridSetting({ viewName, title, gridId: HRCHY_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* FULL Screen 체크 */
  if (fullScreenInfo?.fullScreen) {
    const { targetPosition, targetTitle } = fullScreenInfo;

    if (targetPosition === TOP) {
      const fullDataObject = stackBarData?.[targetTitle];

      return (
        <Box sx={{ display: "flex", width: "100%", height: "100%" }}>
          <TabBox
            key={targetTitle}
            t={t}
            viewName={viewName}
            title={targetTitle}
            data={fullDataObject}
            measureType={measureType}
            stackBarColumDefs={stackBarColumDefs}
            currencyCd={currencyCd}
            hrchyGridRef={hrchyGridRef}
            hrchyGridOptions={defaulHrchysGridMemo}
            fullScreenInfo={fullScreenInfo}
            setFullScreenInfo={setFullScreenInfo}
          />
        </Box>
      );
    } else {
      return (
        <Box sx={{ width: "100%", height: "100%" }}>
          <TabChartGridBox
            t={t}
            viewName={viewName}
            title={targetTitle}
            allMeasures={allMeasures}
            measureType={measureType}
            chartData={chartData}
            gridData={gridData}
            moduleCd={moduleCd}
            fullScreenInfo={fullScreenInfo}
            setFullScreenInfo={setFullScreenInfo}
            currencyCd={currencyCd}
          />
        </Box>
      )
    }
  }

  /* Default Screen (No Full) */
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", height: "100%", minHeight: 0 }}>
      <Box sx={{ display: "flex", gap: 1, width: "100%", flex: 1, minHeight: 0 }}>
        {keys(stackBarData)?.map((stackTitle, _idx) => {
          if (!isEqual(stackTitle, "key")) {
            return (
              <TabBox
                key={stackTitle}
                t={t}
                viewName={viewName}
                title={stackTitle}
                data={stackBarData[stackTitle]}
                measureType={measureType}
                stackBarColumDefs={stackBarColumDefs}
                currencyCd={currencyCd}
                hrchyGridRef={hrchyGridRef}
                hrchyGridOptions={defaulHrchysGridMemo}
                fullScreenInfo={fullScreenInfo}
                setFullScreenInfo={setFullScreenInfo}
              />);
          }
          return null;
        })}
      </Box>
      <Box sx={{ width: "100%", flex: 1, minHeight: 0 }}>
        <TabChartGridBox
          t={t}
          viewName={viewName}
          title={title}
          allMeasures={allMeasures}
          measureType={measureType}
          chartData={chartData}
          gridData={gridData}
          moduleCd={moduleCd}
          fullScreenInfo={fullScreenInfo}
          setFullScreenInfo={setFullScreenInfo}
          currencyCd={currencyCd}
        />
      </Box>
    </Box>
  );

};

const getStackDataByMeasrueType = (data, measureType) => {
  if (!isEmpty(data)) {
    return data.filter(row => isEqual(row.type, measureType));
  }
  return [];
}

/*
 * 상단 Stack Bar 카드.
 *
 * **반드시 모듈 스코프에 둔다.** 부모(DashboardComponent) 본문 안에서 정의하면 부모가 리렌더될
 * 때마다 컴포넌트 타입이 새로 만들어져 안쪽 AgGridReact 가 통째로 unmount/mount 된다. 부모가
 * withTranslation 으로 감싸여 있어 **언어를 바꿀 때마다** 그리드가 다시 마운트되고(스크롤 위치·
 * 컬럼 폭이 초기화된다) 화면이 반짝였다. TabContainer 의 TabPanel 을 모듈 스코프로 올린 것과
 * 같은 이유다.
 */
const TabBox = ({ t, viewName, title, data, styles = {}, measureType, stackBarColumDefs, currencyCd, hrchyGridRef, hrchyGridOptions, fullScreenInfo, setFullScreenInfo }) => {
  const theme = useTheme();
  const reduxDispatch = useDispatch();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const tabsData = keys(data);
  const reduxSelectTab = reduxViewState?.[`${title}Tab`];
  const [selectTab, setSelectTab] = useState(reduxSelectTab ?? tabsData[0]);

  const handleChange = (_, newValue) => {
    setSelectTab(newValue);
    const selectTabParam = { [`${title}Tab`]: newValue };
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...selectTabParam }));
  }

  const hrchyAllRowStyle = (params) => {
    const { data } = params;
    const allHrchyNm = data?.hrchyNm || "";

    if (!isEmpty(allHrchyNm) && allHrchyNm.toUpperCase() === "ALL") {
      return { backgroundColor: alpha(theme.palette.primary.main, 0.1) };
    } else if (params?.rowIndex === 0) {
      return { backgroundColor: alpha(theme.palette.primary.main, 0.1) };
    }

    return {};
  }

  const defaultStackBarColumDefs = [
    {
      headerName: "realCategory", field: "hrchyNm", flex: 2,
      type: ["nonEditableColumn", "defaultValueFormatter"],
    },
    {
      headerName: "",
      field: "stackBarChartData",
      cellRenderer: "stackBarChartCellRenderer",
      cellRendererParams: {
        currencyCd
      },
      type: ["nonEditableColumn", "defaultValueFormatter"],
      flex: 4,
    },
  ];

  return (
    <Box className={"modal-tab"} sx={{ width: "100%", p: 1, ...cardSurfaceSx, ...styles }}>
      <Box sx={{ display: "inline-flex", width: "100%", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Typography key="method2" sx={{ ...cardTitleSx, ml: 0.5 }}>
          {t(title)}
        </Typography>
        <Box>
          <FullScreenButton fullScreenInfo={fullScreenInfo} setFullScreenInfo={setFullScreenInfo} targetPosition={TOP} targetTitle={title} />
        </Box>
      </Box>
      <TabContext value={selectTab} >
        <TabList
          sx={{ justifyContent: "left", ...compactTabsSx }}
          onChange={handleChange}
          scrollButtons="auto"
          variant="scrollable">
          {tabsData?.map(grpKey => {
            let tabName = grpKey;
            if (grpKey === YOY) {
              tabName = YOY_CUM
            }
            return (
              <Tab
                key={grpKey}
                value={grpKey}
                label={t(tabName)}
              />);
          })}
        </TabList>
        {tabsData?.map(tabKey => {
          return (
            <TabPanel
              key={`${tabKey}Child`}
              value={tabKey}
              sx={{ p: 0, height: "calc(100% - 100px)" }}
            >
              <Box className="ag-theme-balham grid-area" mb={1}>
                <AgGridReact
                  ref={hrchyGridRef}
                  rowData={getStackDataByMeasrueType(data[tabKey], measureType)}
                  {...hrchyGridOptions}
                  columnDefs={GridUtils.getColumnDefs(stackBarColumDefs ?? defaultStackBarColumDefs)}
                  rowHeight={35}
                  getRowStyle={hrchyAllRowStyle}
                  components={{ stackBarChartCellRenderer: StackBarChartCellRenderer }}
                />
              </Box>
            </TabPanel>
          );
        })}
      </TabContext>
    </Box>
  );
}

const TabChartGridBox = ({t, viewName, title, allMeasures, measureType, chartData, gridData, moduleCd, fullScreenInfo, setFullScreenInfo, currencyCd}) => {
  const theme = useTheme();
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const reduxSelectTab = reduxViewState?.chartType;
  const TREND_GRID = "trend_grid";
  const { buckets: { MONTH }, toggleType: { AMT } } = CodeUtil;
  const { IP, RP } = CodeUtil.module;
  const userData = JSON.parse(localStorage.getItem("user"));
  const currencySymbol = currencyCd ? CodeUtil.getCurrencySymbol(currencyCd) : userData?.currencySymbol ?? "$";
  const tabsData = ["chart", "data"];
  const { MOM_P, MOM_GP_P, YOY_P, YOY_GP_P } = CodeUtil.dpDashboardMeasure;

  const [baseDates, setBaseDates] = useState(gridData?.baseDate ?? []);
  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [measures, setMeasures] = useState(allMeasures?.[measureType] ?? []);
  const allMeasureForModal = useMemo(() => allMeasures?.[measureType] ?? [], [allMeasures, measureType]);
  const [selectTab, setSelectTab] = useState(reduxSelectTab ?? "chart");
  const [gridLoading, setGridLoading] = useState(false);
  const trendGridRef = useRef();

  const defaultTrendGridMemo = useMemo(() => {
    return DefaultGridSetting({ viewName, title, gridId: TREND_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const measureValCellRenderer = ({ value }) => {
    return t(value);
  };

  const getColumnDefs = useCallback(() => {
    const moduleColumnDefs = [];
    if (isEqual(moduleCd, IP) || isEqual(moduleCd, RP)) {
      moduleColumnDefs.push({ headerName: "location", field: "location", pinned: "left", width: 200 });
    } else {
      moduleColumnDefs.push({ headerName: "customer", field: "custHrchy", pinned: "left", width: 250 });
    }

    const measureValueFormatter = (params, isExcel = false) => {
      const data = params?.data;
      const value = params?.value;
      const measure = data?.measure;

      if (isExcel === true) {
        const field = params?.colDef?.field;
        const excelValue = params?.data?.[field];
        if (data?.measure?.toUpperCase()?.includes("PER")) {
          return numberUtil.formatNumber(excelValue, { maximumFractionDigits: 4, minimumFractionDigits: 0 }).replace(/,/g, "");
        }
        const formattedValue = numberUtil.formatNumberData({ value: excelValue }, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
        return formattedValue.replace(/,/g, "");
      }

      if (value === "-") {
        return value;
      }

      if (isNull(value) || isUndefined(value) || !isFinite(value)) {
        return "";
      }

      if (data?.measure?.toUpperCase()?.includes("PER")) {
        return numberUtil.formatNumberData(params, {
          style: "percent",
          maximumFractionDigits: 2,
          minimumFractionDigits: 0
        });
      }

      if ([INV_TURNOVER, LAST_YEAR_INV_TURNOVER, INV_TURNOVER_COGS, LAST_YEAR_INV_TURNOVER_COGS].includes(measure)) {
        return numberUtil.formatNumberData(params, {maximumFractionDigits: 2, minimumFractionDigits: 0});
      }

      // 각 데이터마다 필요한 필드명이 다를 경우, param.data.totalFields 배열을 활용
      if (measureType === "qty") {
        const formatValue = numberUtil.formatNumberData(params, {maximumFractionDigits: 0, minimumFractionDigits: 0});
        return value >= 0 ? formatValue : `-${formatValue.replace("-", "")}`;
      } else {
        const formatValue = numberUtil.formatNumberData(params, {maximumFractionDigits: 2, minimumFractionDigits: 2});
        return value >= 0 ? currencySymbol + formatValue : `-${currencySymbol}${formatValue.replace("-", "")}`;
      }
    }
    measureValueFormatter.customType = EXCEL_EXPORT;

    // totalValueGetter 개선: totalCustomValue가 있으면 우선 반환
    const totalValueGetter = (param) => {
      const dashTotalList = [ MOM_P, MOM_GP_P, YOY_P, YOY_GP_P, LAST_YEAR_INV_TURNOVER, LAST_YEAR_INV_TURNOVER_COGS, RP_INVENTORY];
      const INV_TURNOVER_MEASURES = [INV_TURNOVER, INV_TURNOVER_COGS];
      const DASH = "-";
      const { context: { headerList }, data } = param;
      if (includes(dashTotalList, data.measure)) {
        return DASH;
      }

      if (data?.customTotal) {
        if (isNaN(data.customTotal) || isNull(data.customTotal) || isFinite(data.customTotal) === false) {
          return 0;
        }
        if (includes(INV_TURNOVER_MEASURES, data?.measure) && data.customTotal !== DASH) {
          return numberUtil.formatNumber(data.customTotal, {maximumFractionDigits: 2, minimumFractionDigits: 0});
        }
        return data.customTotal;
      }

      if (data?.customQtyTotal) {
        if (includes(INV_TURNOVER_MEASURES, data?.measure) && data.customQtyTotal !== DASH) {
          return numberUtil.formatNumber(data.customQtyTotal, {maximumFractionDigits: 2, minimumFractionDigits: 0});
        }
        return data.customQtyTotal;
      }


      if (data?.customAmtTotal) {
        if (isEqual(data.measure, INV_TURNOVER_COGS) || isEqual(data.measure, LAST_YEAR_INV_TURNOVER_COGS)) {
          if (includes(INV_TURNOVER_MEASURES, data?.measure) && data.customCogsTotal !== DASH) {
            return numberUtil.formatNumber(data.customCogsTotal, {maximumFractionDigits: 2, minimumFractionDigits: 0});
          }
          return data.customCogsTotal;
        }
        if (includes(INV_TURNOVER_MEASURES, data?.measure) && data.customAmtTotal !== DASH) {
          return numberUtil.formatNumber(data.customAmtTotal, {maximumFractionDigits: 2, minimumFractionDigits: 0});
        }
        return data.customAmtTotal;
      }

      const actualMeasures = cloneDeep(actualValMeasures);
      if (RP_DASHBOARD !== title) {
        const additionalActualMeasures = ["grossMarginPer", INV_TURNOVER, INV_TURNOVER_COGS];
        actualMeasures.push(...additionalActualMeasures);
      }

      const currentMonthHeaders = includes(actualMeasures, data.measure) ? sliceArrayExcludingCurrentMonth(headerList) : headerList;
      if (data) {
        const totalList = [];
        for (const col of currentMonthHeaders) {
          if (data[col] && !isNaN(data[col])) {
            totalList.push(data[col]);
          } else {
            totalList.push(null);
          }
        }

        return totalList.reduce((a, b) => (Number(a) + Number(b)).toFixed(2), 0);
      }

      return DASH;
    };

    const sliceArrayExcludingCurrentMonth = dates => {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const currentYearMonth = `${year}-${month}`; // ex: '2025-10'

      const targetIndex = dates.findIndex(dateString => {
        const yearMonthInArray = dateString.substring(0, 7);
        return yearMonthInArray === currentYearMonth;
      });

      if (targetIndex !== -1) {
        return dates.slice(0, targetIndex);
      } else {
        return [];
      }
    };

    const avgValueGetter = (param) => {
      const { context: { headerList }, data } = param;
      if (data && headerList) {

        const actualMeasures = cloneDeep(actualValMeasures);
        if (RP_DASHBOARD !== title) {
          const additionalActualMeasures = ["grossMarginPer", INV_TURNOVER, INV_TURNOVER_COGS];
          actualMeasures.push(...additionalActualMeasures);
        }

        const totalList = [];
        const currentMonthHeaders = includes(actualMeasures, data.measure) ? sliceArrayExcludingCurrentMonth(headerList) : headerList;

        for (const col of currentMonthHeaders) {
          if (data[col] && !isNaN(data[col])) {
            totalList.push(data[col]);
          } else {
            totalList.push(null);
          }
        }

        if (totalList.length > 0) {
          return (totalList.reduce((a, b) => a + b, 0) / totalList.length).toFixed(2);
        }
      }

      return "-";
    };

    const defs = [
      ...moduleColumnDefs,
      {
        headerName: "total",
        field: "total",
        editable: false,
        cellStyle: { color: theme.palette.primary.main },
        pinned: "left",
        width: 150,
        type: ["decimalPointColumn", "rightAligned"],
        chartDataType: "series",
        valueGetter: totalValueGetter,
        valueFormatter: measureValueFormatter,
        lockPosition: 'right',
      },
      {
        headerName: "avg",
        field: "avg",
        editable: false,
        cellStyle: { color: theme.palette.primary.main },
        pinned: "left",
        width: 150,
        type: ["decimalPointColumn", "rightAligned"],
        chartDataType: "series",
        valueGetter: avgValueGetter,
        valueFormatter: measureValueFormatter,
        lockPosition: 'right',
      },
      { headerName: "item", field: "itemHrchy", pinned: "left", width: 150 }, {
        headerName: "measure",
        field: "measure",
        width: 250,
        type: ["nonEditableColumn"],
        pinned: "left",
        valueFormatter: measureValCellRenderer,
        headerComponent: CustomHeaderMenuIcon,
        headerComponentParams: {
          enableMenu: false,
          isClicked: setMeasureModalOpen
        }
      },
    ];

    const nowDate = new ZDate();
    const thisMonth = nowDate.getMonth();
    for (const baseDate of baseDates) {
      const base = new ZDate(baseDate);
      const baseMonth = base.getMonth();
      const type = ["numberColumn", "nonEditableColumn"];

      if (isEqual(measureType, AMT)) {
        type.push("numberColumn");
      }

      const cellStyle = () => {
        if (nowDate < base || (thisMonth === baseMonth)) {
          return { backgroundColor: alpha(theme.palette.success.main, 0.12) }
        }

        return {};
      }

      defs.push({
        headerName: dateUtils.headerDateFormatter(baseDate, MONTH), field: baseDate, width: 150,
        valueFormatter: measureValueFormatter,
        colId: GridUtils.IGNORE_P13N_COLLID,
        type: ["numberColumn", "nonEditableColumn", "rightAligned"],
        cellStyle
      })
    }

    return defs;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseDates, measureType]);

  const chartSeries = () => {
    const seriesList = [];
    for (const chartMeasure of measures) {
      let orientation = "left";
      let groupName = t(measureType);
      let type = CodeUtil.toggleType.BAR;
      let formatter = numberUtil.formatNumber;

      if (includes(chartMeasure?.toUpperCase(), "PER")) {
        orientation = "right";
        groupName = t("percent");
        type = CodeUtil.toggleType.LINE;
        formatter = value => numberUtil.formatNumberData({ value }, { style: "percent", maximumFractionDigits: 2, minimumFractionDigits: 0 })
      } else if (isEqual(measureType, AMT) && !includes([LAST_YEAR_INV_TURNOVER, INV_TURNOVER], chartMeasure)) {
        formatter = value => {
          const formatValue = numberUtil.formatNumberData({ value }, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
          return value >= 0 ? currencySymbol + formatValue : `-${currencySymbol}${formatValue.replace("-", "")}`;
        }
      }

      if (includes([LAST_YEAR_INV_TURNOVER, INV_TURNOVER], chartMeasure)) {
        orientation = "right";
        groupName = t(INV_TURNOVER);
        type = CodeUtil.toggleType.LINE;
      }
      const series = {
        field: chartMeasure,
        text: t(chartMeasure),
        type,
        groupName,
        orientation,
        formatter
      }

      if (allMeasures?.[measureType]) {
        if (includes(allMeasures[measureType], chartMeasure)) {
          seriesList.push(series);
        }
      } else {
        seriesList.push(series);
      }
    }

    return seriesList;
  }

  const handleChange = (_, newValue) => {
    setSelectTab(newValue);
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, chartType: newValue }));
  }

  const sortedDataByMeasure = (rowDatas) => {
    const orderMeasures = isEmpty(measures) ? allMeasures?.[measureType] ?? [] : measures;
    if (isEmpty(orderMeasures)) {
      return rowDatas;
    }

    const excludeList = rowDatas?.filter(data => includes(orderMeasures, data?.measure));

    return sortBy(excludeList, obj => indexOf(orderMeasures, obj.measure));
  }

  const applyMeasures = useCallback((selectedMeasures) => {
    setMeasures(selectedMeasures);
  }, [setMeasures]);

  useEffect(() => {
    if (isEqual(selectTab, "data") && gridLoading && !isEmpty(baseDates)) {
      GridUtils.setColumnDefs({ ...trendGridRef.current, columnDefs: getColumnDefs(), viewName, gridId: TREND_GRID, initState: true });
    } else if (isEqual(selectTab, "chart")) {
      setGridLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectTab, gridLoading, baseDates, measureType]);

  useEffect(() => {
    if (!isEmpty(gridData)) {
      setBaseDates(gridData?.baseDate);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData])

  return (
    <Box sx={{ width: "100%", height: "100%", p: 1, ...cardSurfaceSx }}>
      <TabContext value={selectTab}>
        <Box width="100%" display={"inline-flex"} sx={{ justifyContent: 'space-between' }}>
          <TabList
            onChange={handleChange}
            scrollButtons="auto"
            variant="scrollable"
            sx={compactTabsSx}>
            {tabsData?.map(grpKey => {
              let tabName = grpKey;
              if (grpKey === YOY) {
                tabName = YOY_CUM;
              }
              return (
                <Tab
                  key={grpKey}
                  value={grpKey}
                  label={t(tabName)}
                />);
            })}
          </TabList>
          <Box sx={{ pr: 0.5 }}>
            <FullScreenButton fullScreenInfo={fullScreenInfo} setFullScreenInfo={setFullScreenInfo} targetTitle={title} targetPosition={BOTTOM} />
          </Box>
        </Box>
        <TabPanel className="tab-panel" key={"data"} value={"data"} sx={{ height: "100%", p: 0 }}>
          <Box
            className="ag-theme-balham non-background-selected-row"
            mb={1}
            sx={{ height: "calc(100% - 60px)" }}>
            <AgGridReact
              ref={trendGridRef}
              rowData={sortedDataByMeasure(gridData?.[measureType])}
              {...defaultTrendGridMemo}
              onGridReady={_ => setGridLoading(true)}
              groupHideOpenParents
              animateRows
              groupDisplayType={"custom"}
              groupDefaultExpanded={-1}
              context={{ headerList: baseDates }}
              suppressAggFuncInHeader={false}
            />
          </Box>
        </TabPanel>
        <TabPanel className="tab-panel" key={"chart"} value={"chart"} sx={{ height: "calc(100% - 60px)", p: 0 }}>
          <Box className="chart-area">
            <ChartComposed
              xAxis={["yyyymmdd"]}
              data={chartData?.[measureType]}
              dataSeries={chartSeries()}
              tooltipAnimation={false}
              options={{ decimalPoint: true, isAmt: false, currencySymbol }}
            />
          </Box>
        </TabPanel>
      </TabContext>
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasureForModal}
        measureType={measureType}
        applyMeasures={applyMeasures}
      />
    </Box>
  );
}

const FullScreenButton = ({ fullScreenInfo, setFullScreenInfo, targetPosition, targetTitle, ...others }) => {
  const { t } = useTranslation();
  const labelName = "fullscreen";
  const fullScreen = fullScreenInfo?.fullScreen ?? false;
  const tooltipLabel = fullScreen ? "fullScreenOff" : "fullScreen";

  const onClickFullScreen = () => {
    if (!fullScreen) {
      setFullScreenInfo({ targetPosition, targetTitle, fullScreen: !fullScreen });
    } else {
      setFullScreenInfo({ targetPosition: "", targetTitle: "", fullScreen: !fullScreen });
    }
  }

  return (
    <Tooltip title={t(tooltipLabel)}>
      <Box component={"span"}>
        <IconButton aria-label={labelName} color="black" size="small" onClick={() => onClickFullScreen()} {...others}>
          {fullScreen ? <CloseFullscreenIcon sx={{ width: "20px", height: "20px" }} /> : <OpenInFullIcon sx={{ width: "20px", height: "20px" }} />}
        </IconButton>
      </Box>
    </Tooltip>
  );
}

export default withTranslation()(React.memo(DashboardComponent));